import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import { MISSING_LABEL, STATE_LABEL, message, ui, type CanvassRow, type CanvassState } from '../canvass/model.ts'

/**
 * Canvasses — the old NextGen "Campaigns" table (decisions.md 2026-10-25,
 * rows W1 and M1): every canvass with its precincts, houses, voters and
 * walkers, its status, and why a draft cannot be published yet. Actions follow
 * the status, as they did.
 */

/** Status chips: shape and weight carry the state, not a red/green pair. */
const STATE_CLASS: Record<CanvassState, string> = {
  draft: 'border border-dashed border-line text-ink-muted',
  ready: 'border border-brand-400 text-brand-800',
  published: 'bg-brand-900 text-white',
  completed: 'bg-surface-sunken text-ink',
  deactivated: 'border border-line text-ink-muted line-through',
}

function count(n: number | null): string {
  return n == null ? '—' : n.toLocaleString()
}

export function Canvasses() {
  const client = useQueryClient()
  const { campaignId, campaign } = useSelectedCampaign()
  const allowed = campaign?.can_manage_canvassers === true
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['canvasses', campaignId],
    queryFn: () => apiGet<{ canvasses: CanvassRow[] }>(`/campaigns/${campaignId}/canvasses`),
    enabled: Boolean(campaignId) && allowed,
  })

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'publish' | 'deactivate' | 'delete' }) =>
      apiPost(`/campaigns/${campaignId}/canvasses/${id}/${action}`),
    onSuccess: async () => {
      setError(null)
      setConfirmDelete(null)
      await client.invalidateQueries({ queryKey: ['canvasses', campaignId] })
    },
    onError: (caught) => setError(message(caught)),
  })

  const rows = list.data?.canvasses ?? []

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Canvasses</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Each canvass is one walk effort: who to walk, who walks it, and what to ask at the door.
          </p>
        </div>
        {allowed && campaign?.may_cut ? (
          <Link to="/canvasses/new" className={ui.primary}>
            New canvass
          </Link>
        ) : null}
      </div>

      {campaign && !allowed ? (
        <p className="mt-6 text-sm text-ink-muted">Only the Campaign Manager can run canvasses on this campaign.</p>
      ) : null}
      {campaign && allowed && !campaign.may_cut ? (
        <p role="status" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
          New canvasses stopped on election day. Results stay open on each canvass.
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}

      {allowed ? (
        list.isPending ? (
          <p className="mt-6 text-sm text-ink-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-line p-6 text-sm">
            <p className="font-medium">No canvasses yet.</p>
            <p className="mt-1 text-ink-muted">
              Start one with <strong>New canvass</strong>: name it, pick the precincts, give them to
              walkers, choose the questions, and publish.
            </p>
          </div>
        ) : (
          <div className="relative mt-6 overflow-x-auto rounded-lg border border-line">
            <table className="w-full min-w-[46rem] text-sm">
              <thead className="bg-surface-sunken text-left text-xs tracking-wide text-ink-muted uppercase">
                <tr>
                  <th className="px-3 py-2 font-medium">Canvass</th>
                  <th className="px-3 py-2 text-right font-medium">Precincts</th>
                  <th className="px-3 py-2 text-right font-medium">Houses</th>
                  <th className="px-3 py-2 text-right font-medium">Voters</th>
                  <th className="px-3 py-2 text-right font-medium">Walkers</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line tabular-nums">
                {rows.map((c) => (
                  <tr key={c.canvass_id} className="align-top">
                    <td className="px-3 py-2.5">
                      <Link
                        to="/canvasses/$canvassId"
                        params={{ canvassId: c.canvass_id }}
                        className="font-medium text-ink hover:underline"
                      >
                        {c.name}
                      </Link>
                      <div className="text-xs text-ink-muted">
                        {c.end_date ? `ends ${c.end_date}` : 'no end date'}
                        {c.target_contacts ? ` · target ${c.target_contacts.toLocaleString()} contacts` : ''}
                        {c.paper_groups > 0 ? ` · ${c.paper_groups} on paper` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">{count(c.precincts)}</td>
                    <td className="px-3 py-2.5 text-right">{count(c.doors)}</td>
                    <td className="px-3 py-2.5 text-right">{count(c.voters)}</td>
                    <td className="px-3 py-2.5 text-right">{c.walkers}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_CLASS[c.state]}`}>
                        {STATE_LABEL[c.state]}
                      </span>
                      {c.missing.length > 0 ? (
                        <ul className="mt-1 space-y-0.5 text-xs text-ink-muted">
                          {c.missing.map((m) => (
                            <li key={m}>{MISSING_LABEL[m] ?? m}</li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Link
                          to="/canvasses/$canvassId"
                          params={{ canvassId: c.canvass_id }}
                          className={ui.secondary}
                        >
                          {c.state === 'draft' || c.state === 'ready' ? 'Continue' : 'Open'}
                        </Link>
                        {c.state === 'ready' || c.state === 'deactivated' ? (
                          <button
                            type="button"
                            className={ui.secondary}
                            disabled={act.isPending}
                            onClick={() => act.mutate({ id: c.canvass_id, action: 'publish' })}
                          >
                            {c.state === 'ready' ? 'Publish' : 'Activate'}
                          </button>
                        ) : null}
                        {c.state === 'published' ? (
                          <button
                            type="button"
                            className={ui.secondary}
                            disabled={act.isPending}
                            onClick={() => act.mutate({ id: c.canvass_id, action: 'deactivate' })}
                          >
                            Deactivate
                          </button>
                        ) : null}
                        {confirmDelete === c.canvass_id ? (
                          <span className="flex items-center gap-1.5">
                            <button
                              type="button"
                              className={ui.secondary}
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: c.canvass_id, action: 'delete' })}
                            >
                              Yes, delete
                            </button>
                            <button
                              type="button"
                              className="text-xs text-ink-muted hover:text-ink"
                              onClick={() => setConfirmDelete(null)}
                            >
                              Keep
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="px-1 text-xs text-ink-muted hover:text-ink"
                            onClick={() => setConfirmDelete(c.canvass_id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
      {allowed && rows.some((c) => c.state === 'published') ? (
        <p className="mt-3 max-w-prose text-xs text-ink-muted">
          A published canvass reaches its walkers' phones within a minute. Changes you make to
          its groups reach them the same way.
        </p>
      ) : null}
    </div>
  )
}
